import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, Plus, Trash2, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import AirportSearch from "@/components/AirportSearch";
import PassengerSelector from "@/components/PassengerSelector";
import PublicLayout from "@/components/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://thebetterfare-backend-production.up.railway.app'}/api`;
const HERO_IMAGE = "/images/flight.png";
const WHATSAPP_GROUP = "https://chat.whatsapp.com/FTpF0TyZG2nJkWpWKPdlrM?mode=hqrc";
const TURNSTILE_SITE_KEY = "0x4AAAAAAC8k_BC0s3zVYBE5";


export default function FlightRequestForm() {
  useEffect(() => { document.title = "Request a Flight Quote | The Better Fare"; }, []);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestNumber, setRequestNumber] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [departureOpen, setDepartureOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

 
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    travel_type: "return",
    travel_class: "economy",
    baggage: "checked_baggage",
    passengers: { adults: 1, children: 0, infants: 0 },
    departure_airport: "",
    arrival_airport: "",
    departure_date: "",
    return_date: "",
    departure_flexibility: 0,
    return_flexibility: 0,
    customer_notes: "",
    segments: [
      { departure_airport: "", arrival_airport: "", departure_date: "" }
    ]
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSegmentChange = (index, field, value) => {
    setFormData(prev => {
      const newSegments = [...prev.segments];
      newSegments[index] = { ...newSegments[index], [field]: value };
      return { ...prev, segments: newSegments };
    });
  };

  const addSegment = () => {
    if (formData.segments.length < 6) {
      setFormData(prev => ({
        ...prev,
        segments: [...prev.segments, { departure_airport: "", arrival_airport: "", departure_date: "" }]
      }));
    }
  };

  const removeSegment = (index) => {
    if (formData.segments.length > 1) {
      setFormData(prev => ({
        ...prev,
        segments: prev.segments.filter((_, i) => i !== index)
      }));
    }
  };

  const handleTravelTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      travel_type: type,
      segments: type === "multi_city" ? prev.segments : [{ departure_airport: "", arrival_airport: "", departure_date: "" }]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let segments = [];

      if (formData.travel_type === "one_way") {
        if (!formData.departure_airport || !formData.arrival_airport || !formData.departure_date) {
          toast.error("Please fill in all flight details");
          setLoading(false);
          return;
        }
        segments = [{
          departure_airport: formData.departure_airport,
          arrival_airport: formData.arrival_airport,
          departure_date: formData.departure_date
        }];
      } else if (formData.travel_type === "return") {
        if (!formData.departure_airport || !formData.arrival_airport || !formData.departure_date || !formData.return_date) {
          toast.error("Please fill in all flight details including return date");
          setLoading(false);
          return;
        }
        segments = [
          {
            departure_airport: formData.departure_airport,
            arrival_airport: formData.arrival_airport,
            departure_date: formData.departure_date
          },
          {
            departure_airport: formData.arrival_airport,
            arrival_airport: formData.departure_airport,
            departure_date: formData.return_date
          }
        ];
      } else {
        segments = formData.segments.filter(seg =>
          seg.departure_airport && seg.arrival_airport && seg.departure_date
        );
        if (segments.length === 0) {
          toast.error("Please fill in at least one flight segment");
          setLoading(false);
          return;
        }
      }

      const response = await axios.post(`${API}/flight-requests`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        travel_type: formData.travel_type,
        travel_class: formData.travel_class,
        baggage: formData.baggage,
        passengers: formData.passengers,
        segments,
        departure_flexibility: formData.departure_flexibility,
        return_flexibility: formData.return_flexibility,
        customer_notes: formData.customer_notes || null,
        consent_given: consentGiven,
        turnstile_token: turnstileToken,
      });

      setRequestNumber(response.data.request_number);
      setSubmitted(true);
      toast.success("Flight request submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setRequestNumber("");
    setTurnstileToken("");
    setFormData({
      first_name: "", last_name: "", email: "", phone: "",
      travel_type: "return", travel_class: "economy", baggage: "checked_baggage",
      passengers: { adults: 1, children: 0, infants: 0 },
      departure_airport: "", arrival_airport: "", departure_date: "", return_date: "",
      departure_flexibility: 0, return_flexibility: 0, customer_notes: "",
      segments: [{ departure_airport: "", arrival_airport: "", departure_date: "" }]
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 900 }}>
            Request Submitted!
          </h2>
          <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200">
            <p className="text-sm text-gray-500 mb-2">Your Request Number</p>
            <p className="text-3xl font-mono font-bold text-gray-900 tracking-wider">{requestNumber}</p>
          </div>
          <p className="text-gray-600 mb-8">
            Thank you for your flight request. We've sent a confirmation to your email. One of our travel experts will contact you shortly with the best available options.
          </p>
          <Button
            onClick={resetForm}
            className="tbf-submit-btn rounded-md px-8 h-12"
            data-testid="submit-another-btn"
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PublicLayout>
      <div data-testid="flight-request-page">
        {/* Page Title */}
        <div className="text-center pt-10 pb-6">
          <h1 className="tbf-page-title" data-testid="page-title">FLIGHT REQUEST</h1>
        </div>

        {/* Hero Section */}
        <div className="max-w-6xl mx-auto px-6 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="tbf-hero-heading mb-2">Ready to take off on your</h2>
              <h2 className="tbf-hero-heading-accent mb-6">next adventure?</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Whether you're planning a quick getaway or a long-haul journey, we're here to make it easy. Simply fill out the form below, and our expert team will find you the best flights at unbeatable prices.
              </p>
              <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1.3em", fontWeight: "700", color: "#1a1a1a" }} className="mb-3">
                Your hassle-free travel experience starts here!
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                To ensure seamless communication, please join the WhatsApp group using the same number you provide in the form. This allows us to connect with you quickly and efficiently.
              </p>
              <a
                href={WHATSAPP_GROUP}
                target="_blank"
                rel="noopener noreferrer"
                className="tbf-whatsapp-btn"
                data-testid="whatsapp-group-btn"
              >
                The Better Fare | Whatsapp Group
              </a>
            </div>
            <div className="hidden lg:flex justify-center">
              <img src={HERO_IMAGE} alt="Travel illustration" className="w-full max-w-md" />
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="tbf-form-section" data-testid="flight-request-form">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Row 1: Personal Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <label className="tbf-label">First Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.first_name} onChange={(e) => handleInputChange("first_name", e.target.value)} required placeholder="E.g. John" className="tbf-input" data-testid="first-name-input" />
                </div>
                <div>
                  <label className="tbf-label">Last Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.last_name} onChange={(e) => handleInputChange("last_name", e.target.value)} required placeholder="E.g. Doe" className="tbf-input" data-testid="last-name-input" />
                </div>
                <div>
                  <label className="tbf-label">Email <span className="text-red-500">*</span></label>
                  <input type="email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} required placeholder="E.g. john@doe.com" className="tbf-input" data-testid="email-input" />
                </div>
                <div>
                  <label className="tbf-label">Phone <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} required placeholder="E.g. +31 6 1234567" className="tbf-input" data-testid="phone-input" />
                </div>
              </div>

              {/* Row 2: Travel Options */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div>
                  <label className="tbf-label">Travel Type <span className="text-red-500">*</span></label>
                  <Select value={formData.travel_type} onValueChange={handleTravelTypeChange}>
                    <SelectTrigger className="tbf-select-trigger" data-testid="travel-type-select">
                      <SelectValue placeholder="Select Travel Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_way">One Way</SelectItem>
                      <SelectItem value="return">Return</SelectItem>
                      <SelectItem value="multi_city">Multi City</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="tbf-label">Travel Class</label>
                  <Select value={formData.travel_class} onValueChange={(v) => handleInputChange("travel_class", v)}>
                    <SelectTrigger className="tbf-select-trigger" data-testid="travel-class-select">
                      <SelectValue placeholder="Select Travel Class" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="economy">Economy</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="first">First Class</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="tbf-label">Baggage <span className="text-red-500">*</span></label>
                  <Select value={formData.baggage} onValueChange={(v) => handleInputChange("baggage", v)}>
                    <SelectTrigger className="tbf-select-trigger" data-testid="baggage-select">
                      <SelectValue placeholder="Select Option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_baggage">No Baggage or Hand bag / laptop bag only</SelectItem>
                      <SelectItem value="hand_carry">7kg hand Carry</SelectItem>
                      <SelectItem value="checked_baggage">Checked baggage included</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 3: Passengers */}
              <div>
                <label className="tbf-label mb-3 block">Tickets</label>
                <PassengerSelector
                  passengers={formData.passengers}
                  onChange={(passengers) => setFormData(prev => ({ ...prev, passengers }))}
                />
              </div>

              {/* Row 4: Airports & Dates */}
              {(formData.travel_type === "one_way" || formData.travel_type === "return") && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="tbf-label">Departure Airport <span className="text-red-500">*</span></label>
                      <div className="mt-1">
                        <AirportSearch value={formData.departure_airport} onChange={(code) => handleInputChange("departure_airport", code)} placeholder="Search departure airport..." testId="departure-airport-search" />
                      </div>
                    </div>
                    <div>
                      <label className="tbf-label">Arrival Airport <span className="text-red-500">*</span></label>
                      <div className="mt-1">
                        <AirportSearch value={formData.arrival_airport} onChange={(code) => handleInputChange("arrival_airport", code)} placeholder="Search arrival airport..." testId="arrival-airport-search" />
                      </div>
                    </div>
                  </div>

                  <div className={`grid ${formData.travel_type === "return" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"} gap-5`}>
                    <div className="space-y-3">
                      <div>
                        <label className="tbf-label">Departure Date <span className="text-red-500">*</span></label>
                        <Popover open={departureOpen} onOpenChange={setDepartureOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="tbf-date-btn" data-testid="departure-date-btn">
                              <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                              {formData.departure_date ? format(new Date(formData.departure_date), "PPP") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.departure_date ? new Date(formData.departure_date) : undefined}
                              onSelect={(date) => {
                                handleInputChange("departure_date", date ? format(date, "yyyy-MM-dd") : "");
                                setDepartureOpen(false);
                              }}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="tbf-label text-xs text-gray-500">Date Flexibility</label>
                        <Select value={String(formData.departure_flexibility)} onValueChange={(v) => handleInputChange("departure_flexibility", parseInt(v))}>
                          <SelectTrigger className="tbf-select-trigger h-9 text-sm" data-testid="departure-flexibility-select">
                            <SelectValue placeholder="Exact date" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Exact date only</SelectItem>
                            <SelectItem value="1">± 1 day flexible</SelectItem>
                            <SelectItem value="2">± 2 days flexible</SelectItem>
                            <SelectItem value="3">± 3 days flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {formData.travel_type === "return" && (
                      <div className="space-y-3">
                        <div>
                          <label className="tbf-label">Return Date</label>
                          <Popover open={returnOpen} onOpenChange={setReturnOpen}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="tbf-date-btn" data-testid="return-date-btn">
                                <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                                {formData.return_date ? format(new Date(formData.return_date), "PPP") : "Select date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={formData.return_date ? new Date(formData.return_date) : undefined}
                                onSelect={(date) => {
                                  handleInputChange("return_date", date ? format(date, "yyyy-MM-dd") : "");
                                  setReturnOpen(false);
                                }}
                                disabled={(date) => {
                                  const minDate = formData.departure_date ? new Date(formData.departure_date) : new Date();
                                  return date < minDate;
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className="tbf-label text-xs text-gray-500">Date Flexibility</label>
                          <Select value={String(formData.return_flexibility)} onValueChange={(v) => handleInputChange("return_flexibility", parseInt(v))}>
                            <SelectTrigger className="tbf-select-trigger h-9 text-sm" data-testid="return-flexibility-select">
                              <SelectValue placeholder="Exact date" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">Exact date only</SelectItem>
                              <SelectItem value="1">± 1 day flexible</SelectItem>
                              <SelectItem value="2">± 2 days flexible</SelectItem>
                              <SelectItem value="3">± 3 days flexible</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Multi City Segments */}
              {formData.travel_type === "multi_city" && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <label className="tbf-label">Flight Segments ({formData.segments.length}/6)</label>
                    <Button type="button" variant="ghost" size="sm" onClick={addSegment} disabled={formData.segments.length >= 6} className="text-[#6837B8] hover:text-[#5a2ea0]" data-testid="add-segment-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Segment
                    </Button>
                  </div>
                  {formData.segments.map((segment, index) => (
                    <div key={index} className="p-5 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Flight {index + 1}</span>
                        {formData.segments.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeSegment(index)} className="text-red-500" data-testid={`remove-segment-${index}-btn`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="tbf-label text-xs">Departure Airport <span className="text-red-500">*</span></label>
                          <div className="mt-1">
                            <AirportSearch value={segment.departure_airport} onChange={(code) => handleSegmentChange(index, "departure_airport", code)} placeholder="Search airport..." testId={`departure-airport-${index}-search`} />
                          </div>
                        </div>
                        <div>
                          <label className="tbf-label text-xs">Arrival Airport <span className="text-red-500">*</span></label>
                          <div className="mt-1">
                            <AirportSearch value={segment.arrival_airport} onChange={(code) => handleSegmentChange(index, "arrival_airport", code)} placeholder="Search airport..." testId={`arrival-airport-${index}-search`} />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="tbf-label text-xs">Departure Date <span className="text-red-500">*</span></label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="tbf-date-btn" data-testid={`departure-date-${index}-btn`}>
                              <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                              {segment.departure_date ? format(new Date(segment.departure_date), "PPP") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={segment.departure_date ? new Date(segment.departure_date) : undefined}
                              onSelect={(date) => handleSegmentChange(index, "departure_date", date ? format(date, "yyyy-MM-dd") : "")}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Customer Notes */}
              <div>
                <label className="tbf-label">Additional Comments or Special Requests</label>
                <textarea
                  value={formData.customer_notes}
                  onChange={(e) => handleInputChange("customer_notes", e.target.value)}
                  placeholder="E.g., preferred airlines, seat preferences, special assistance needs, layover preferences..."
                  rows={3}
                  className="tbf-input resize-none"
                  data-testid="customer-notes-input"
                />
              </div>

              {/* Turnstile */}
              <div>
               <div
  className="cf-turnstile"
  data-sitekey={TURNSTILE_SITE_KEY}
  data-callback="onTurnstileSuccess"
  ref={(el) => {
    if (el && window.turnstile && !el.dataset.rendered) {
      el.dataset.rendered = "true";
      window.turnstile.render(el, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
      });
    }
  }}
/>
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consentGiven}
                  onCheckedChange={setConsentGiven}
                  className="mt-0.5 border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                  data-testid="consent-checkbox"
                />
                <label htmlFor="consent" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                  By submitting this form, you agree to our{" "}
                  <Link to="/terms" className="text-purple-600 hover:underline font-medium">Terms and Conditions</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="text-purple-600 hover:underline font-medium">Privacy Policy</Link>.
                </label>
              </div>

              <Button
                type="submit"
                className="tbf-submit-btn w-full h-14 text-base font-semibold"
                disabled={loading || !consentGiven || !turnstileToken}
                data-testid="submit-flight-request-btn"
              >
                {loading ? "Submitting..." : "Get Best Flight Deal"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
